import React, { useEffect, useRef } from 'react';

interface SessionTimeoutWarningProps {
  secondsRemaining: number;
  onStayLoggedIn: () => void;
  onLogout: () => void;
}

/**
 * Session Timeout Warning Modal
 * Displays a countdown when the user's session is about to expire.
 * Implements focus trap and ARIA alertdialog for accessibility.
 */
const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  secondsRemaining,
  onStayLoggedIn,
  onLogout,
}) => {
  const stayButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus the "Stay Logged In" button when modal opens
  useEffect(() => {
    stayButtonRef.current?.focus();
  }, []);

  // Focus trap: keep Tab/Shift+Tab inside the modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onStayLoggedIn();
        return;
      }

      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onStayLoggedIn]);

  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  const timeDisplay = minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, '0')}`
    : `${seconds} seconds`;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      role="presentation"
    >
      <div
        ref={modalRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="timeout-title"
        aria-describedby="timeout-desc"
        style={{
          backgroundColor: '#1f2937',
          border: '1px solid #D4A017',
          borderRadius: '0.75rem',
          padding: '2rem',
          maxWidth: '420px',
          width: '90%',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>
          &#9888;
        </div>
        <h2
          id="timeout-title"
          style={{
            color: '#D4A017',
            fontSize: '1.25rem',
            marginBottom: '0.75rem',
          }}
        >
          Session Expiring
        </h2>
        <p
          id="timeout-desc"
          style={{
            color: '#d1d5db',
            fontSize: '1rem',
            marginBottom: '0.5rem',
          }}
        >
          Your session will expire in{' '}
          <strong
            style={{ color: '#ef4444' }}
            aria-live="polite"
            aria-atomic="true"
          >
            {timeDisplay}
          </strong>{' '}
          due to inactivity.
        </p>
        <p style={{ color: '#d1d5db', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
          Any unsaved changes may be lost. Click below to stay logged in.
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button
            ref={stayButtonRef}
            onClick={onStayLoggedIn}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#D4A017',
              color: '#000',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem',
            }}
          >
            Stay Logged In
          </button>
          <button
            onClick={onLogout}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#374151',
              color: '#d1d5db',
              border: '1px solid #4b5563',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Log Out Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
